import React, { createContext, FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import EnterpriseComplianceDashboardService from "../enterpriseComplianceDashboardService";
import { DropdownItem, NestingParentDropdownItem } from "@app/shared/checkbox-dropdown/types";
import { DisplayFilters, EcdApiFilters, EcdFilterStorageItem, TimeFrameOptions } from "../interfaces";
import { RegionSetStatusType } from "@app/projects/enumerators";
import { FilterItem, FilterItemsGroup } from "@app/resource-filters/filter-items";
import { calculateServiceAreaSelections } from "@app/shared/service-area-dropdown/helpers";
import { FilterItemsGroupOperation, FilterItemType } from "@app/resource-filters/common";
import { ResourceFiltrationService } from "@app/projects/resource-filtration";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { UserContext } from "@app/user/UserContext";
import { areSetsEquivalent } from "@app/shared/utility/areSetsEquivalent";

export interface ApplyFiltersOverrides {
  filterCountyStatus?: Array<DropdownItem<string>>;
  filterLineOfBusiness?: Array<DropdownItem<string>>;
  filterProjects?: Array<DropdownItem<string>>;
  filterServiceArea?: string[];
  filterSpecialty?: Array<DropdownItem<string>>;
  filterTags?: string[];
}

export interface EnterpriseComplianceFiltersContextValues {
  apiReadyFilters: EcdApiFilters;
  applyCustomFilterSet: (filters: EcdFilterStorageItem) => void;
  applyFilters: (emptyFilters?: boolean, overrides?: ApplyFiltersOverrides) => void;
  resetFilters: () => void;
  countyStatusOptions: Array<DropdownItem<string>>,
  deleteCustomFilterSets: (names: string[]) => void;
  displayFilters: DisplayFilters;
  exceptionsTimeFrame: TimeFrameOptions,
  // project ids excluded from the data by selected tags
  excludedProjectIds: number[];
  filterTags: string[];
  filterLineOfBusiness: Array<DropdownItem<string>>;
  filterServiceArea: string[];
  filterSpecialty: Array<DropdownItem<string>>;
  filterCountyStatus: Array<DropdownItem<string>>;
  filterProjects: Array<DropdownItem<string>>;
  filterSetNames: string[];
  forceNoResults: boolean;
  gapOverviewTimeFrame: TimeFrameOptions;
  gapsByAgeTimeFrame: TimeFrameOptions;
  getFilterSetNames: () => string[];
  hasLoadedFiltersFromStorage: boolean;
  isDashboardLoading: boolean;
  loadCustomFilterSet: (filterSetName?: string) => EcdFilterStorageItem;
  loadedFilterSet: string;
  LOBOptions: Array<DropdownItem<string>>;
  projectsOptions: Array<DropdownItem<string>>;
  saveCustomFilterSet: (filterSetName: string, filters?: EcdFilterStorageItem) => void;
  serviceAreaOptions: NestingParentDropdownItem[];
  setDisplayFilters: React.Dispatch<React.SetStateAction<DisplayFilters>>,
  setExceptionsTimeFrame: React.Dispatch<React.SetStateAction<TimeFrameOptions>>,
  setFilterTags: (value: string[]) => void;
  setFilterLineOfBusiness: React.Dispatch<React.SetStateAction<Array<DropdownItem<string>>>>;
  setFilterServiceArea: React.Dispatch<React.SetStateAction<string[]>>;
  setFilterSpecialty: React.Dispatch<React.SetStateAction<Array<DropdownItem<string>>>>;
  setFilterCountyStatus: React.Dispatch<React.SetStateAction<Array<DropdownItem<string>>>>;
  setFilterProjects: React.Dispatch<React.SetStateAction<Array<DropdownItem<string>>>>;
  setGapOverviewTimeFrame: React.Dispatch<React.SetStateAction<TimeFrameOptions>>,
  setGapsByAgeTimeFrame: React.Dispatch<React.SetStateAction<TimeFrameOptions>>,
  setIsDashboardLoading: React.Dispatch<React.SetStateAction<boolean>>;
  specialtyOptions: Array<DropdownItem<string>>;
  tagsOptions: NestingParentDropdownItem[];
}

const initialValues: EnterpriseComplianceFiltersContextValues = {
  apiReadyFilters: null,
  applyCustomFilterSet: () => null,
  applyFilters: () => null,
  countyStatusOptions: new Array<DropdownItem<string>>(),
  deleteCustomFilterSets: () => null,
  displayFilters: null,
  exceptionsTimeFrame: TimeFrameOptions.Twelve,
  excludedProjectIds: [],
  filterCountyStatus: new Array<DropdownItem<string>>(),
  filterLineOfBusiness: new Array<DropdownItem<string>>(),
  filterProjects: new Array<DropdownItem<string>>(),
  filterServiceArea: [],
  filterSetNames: [],
  filterSpecialty: new Array<DropdownItem<string>>(),
  filterTags: [],
  forceNoResults: false,
  gapOverviewTimeFrame: TimeFrameOptions.Twelve,
  gapsByAgeTimeFrame: TimeFrameOptions.Twelve,
  getFilterSetNames: () => [],
  hasLoadedFiltersFromStorage: false,
  isDashboardLoading: true,
  loadCustomFilterSet: () => null,
  loadedFilterSet: undefined,
  LOBOptions: new Array<DropdownItem<string>>(),
  projectsOptions: null,
  resetFilters: () => null,
  saveCustomFilterSet: () => null,
  serviceAreaOptions: [],
  setDisplayFilters: () => null,
  setExceptionsTimeFrame: () => null,
  setFilterCountyStatus: () => null,
  setFilterLineOfBusiness: () => null,
  setFilterProjects: () => null,
  setFilterServiceArea: () => null,
  setFilterSpecialty: () => null,
  setFilterTags: () => null,
  setGapOverviewTimeFrame: () => null,
  setGapsByAgeTimeFrame: () => null,
  setIsDashboardLoading: () => null,
  specialtyOptions: new Array<DropdownItem<string>>(),
  tagsOptions: [],
}

export const EnterpriseComplianceFiltersContext = createContext(initialValues);

const EnterpriseComplianceFiltersContextProvider: FC<{ children: React.ReactNode }> = (props: { children: React.ReactNode }) => {
  /* Constants */
  const userId = useRef(useContext(UserContext).userId).current;
  const localStorageKey = useRef(`${userId}_enterprise-compliance-dashboard-filters`).current;
  const defaultCountyStatuses = useRef(["Active", "Pending", "Approved"]).current;
  const featureFlags = useContext(FeatureFlagContext).enabledFeatureFlags;
  const enterpriseComplianceDashboardService = useRef(new EnterpriseComplianceDashboardService()).current;
  const resourceFiltrationService = useRef(new ResourceFiltrationService()).current;

  /* Filter Options */
  // We have a whole bunch of arrays in state here
  // Be extra careful to not modify any of these in place
  // because it will make it difficult to troubleshoot anything
  const [tagsOptions, setTagsOptions] = useState<NestingParentDropdownItem[]>([]);
  const [LOBOptions, setLOBOptions] = useState<Array<DropdownItem<string>>>([]);
  const [serviceAreaOptions, setServiceAreaOptions] = useState<NestingParentDropdownItem[]>([]);
  const [specialtyOptions, setSpecialtyOptions] = useState<Array<DropdownItem<string>>>([]);
  const [countyStatusOptions, setCountyStatusOptions] = useState<Array<DropdownItem<string>>>([]);
  const [projectsOptions, setProjectsOptions] = useState<Array<DropdownItem<string>>>([]);

  /* Selected Filter Values (and Related) */
  const [filterTags, setFilterTags] = useState<string[]>(initialValues.filterTags);
  const [filterLineOfBusiness, setFilterLineOfBusiness] = useState<Array<DropdownItem<string>>>(initialValues.filterLineOfBusiness);
  const [filterServiceArea, setFilterServiceArea] = useState<string[]>(initialValues.filterServiceArea);
  const [filterSpecialty, setFilterSpecialty] = useState<Array<DropdownItem<string>>>(initialValues.filterSpecialty);
  const [filterCountyStatus, setFilterCountyStatus] = useState<Array<DropdownItem<string>>>(initialValues.filterCountyStatus);
  const [filterProjects, setFilterProjects] = useState<Array<DropdownItem<string>>>(initialValues.filterProjects);
  const [apiReadyFilters, setApiReadyFilters] = useState(initialValues.apiReadyFilters);
  const [forceNoResults, setForceNoResults] = useState(initialValues.forceNoResults);
  const [excludedProjectIds, setExcludedProjectIds] = useState(initialValues.excludedProjectIds);
  const [displayFilters, setDisplayFilters] = useState(initialValues.displayFilters);

  /* Miscellaneous */
  const [isDashboardLoading, setIsDashboardLoading] = useState(initialValues.isDashboardLoading);
  // if we had a flowchart for loading the dashboard, there'd be a split on the initial load
  // based on whether there are filters saved in local storage. If there are, we use those.
  // If there aren't, we calculate the default county statuses based on which options were returned.
  // Calling applyFilters for the wrong one would mess up the saved filters.
  const [hasLoadedFiltersFromStorage, setHasLoadedFiltersFromStorage] = useState(initialValues.hasLoadedFiltersFromStorage);
  const [filterSetNames, setFilterSetNames] = useState<string[]>(initialValues.filterSetNames);
  const [loadedFilterSet, setLoadedFilterSet] = useState<string>(initialValues.loadedFilterSet);

  /* Timeframe States for Exports */
  const [exceptionsTimeFrame, setExceptionsTimeFrame] = useState<TimeFrameOptions>(initialValues.exceptionsTimeFrame);
  const [gapOverviewTimeFrame, setGapOverviewTimeFrame] = useState<TimeFrameOptions>(initialValues.gapOverviewTimeFrame);
  const [gapsByAgeTimeFrame, setGapsByAgeTimeFrame] = useState<TimeFrameOptions>(initialValues.gapsByAgeTimeFrame);

  /* Internal State Values */
  const [hasSetInitialCountyStatuses, setHasSetInitialCountyStatuses] = useState(false);
  const [hasLoadedFilterOptions, setHasLoadedFilterOptions] = useState(false);

  useEffect(() => {
    const promises = [
      resourceFiltrationService.getTagsFilterOptions(),
      enterpriseComplianceDashboardService.getLOBFilterOptions(),
      enterpriseComplianceDashboardService.getServiceAreaOptions(),
      enterpriseComplianceDashboardService.getSpecialtyFilterOptions(),
      enterpriseComplianceDashboardService.getCountyStatusFilterOptions(),
      enterpriseComplianceDashboardService.getProjectsFilterOptions(),
    ];

    Promise.all(promises).then((responses) => {
      const [tags, lob, serviceArea, specialty, status, projects] = responses;

      setTagsOptions(convertTagStringsToFilters(tags as Partial<FilterItem>[]));
      setLOBOptions(lob as DropdownItem<string>[]);
      setServiceAreaOptions(serviceArea as NestingParentDropdownItem[]);
      setSpecialtyOptions(specialty as DropdownItem<string>[]);
      setCountyStatusOptions(status as DropdownItem<string>[]);
      setProjectsOptions(projects as DropdownItem<string>[]);
      setHasLoadedFilterOptions(true);
    });
  }, [featureFlags]);

  useEffect(() => {
    getFilterSetNames();
  }, []);

  useEffect(() => {
    if (hasLoadedFilterOptions) {
      const loadedFilters = loadCustomFilterSet();
      // if the user only has the defaults saved, we might as well update the defaults
      // just in case new options were added, like a new service area from a project
      if (!loadedFilters || loadedFilters?.isDefaultFilters) {
        const defaults = defaultFilterSelections;

        setFilterLineOfBusiness(defaults.selectedLOBs);
        setFilterServiceArea(defaults.selectedServiceAreas);
        setFilterSpecialty(defaults.selectedSpecialties);
        setFilterCountyStatus(defaults.selectedStatuses);
        setFilterProjects(defaults.selectedProjects);
        setHasSetInitialCountyStatuses(true);
      } else {
        applyCustomFilterSet(loadedFilters);
        setHasLoadedFiltersFromStorage(true);
      }
    }
  }, [hasLoadedFilterOptions]);

  // because we auto-set county status filters on load, we have to apply them
  useEffect(() => {
    if (hasSetInitialCountyStatuses && filterCountyStatus.length > 0) {
      applyFilters();
    }
  }, [hasSetInitialCountyStatuses]);

  // We can't call loadCustomFilterSet and applyFilters in the same method
  // because the filters won't have been updated in state before applyFilters
  // applies the filters in state...so we use useEffect to pick it up on that next render
  useEffect(() => {
    if (hasLoadedFiltersFromStorage) {
      applyFilters();
    }
  }, [hasLoadedFiltersFromStorage]);

  useEffect(() => {
    const selectedFilterSet = getFiltersSavedSet();
    setLoadedFilterSet(selectedFilterSet);
  }, [
    apiReadyFilters
  ]);

  // save the filters to local storage
  // this is only called when the apply button is clicked,
  // because if we used a useEffect for every time they changed,
  // it'd get overwritten on every component mount
  // this is also called on submitting saved filter set name changes
  const saveFiltersToLocalStorage = (filterSetName?: string, emptyFilters?: boolean, savedFilters?: EcdFilterStorageItem, overrides?: ApplyFiltersOverrides) => {
    const counties = emptyFilters ? defaultFilterSelections.selectedServiceAreas :
      savedFilters ? savedFilters.counties :
        overrides?.filterServiceArea ?? filterServiceArea;

    const countyStatuses = emptyFilters ? defaultFilterSelections.selectedStatuses :
      savedFilters ? savedFilters.countyStatuses :
        overrides?.filterCountyStatus ?? filterCountyStatus;

    const networkTaxonomyLOBs = emptyFilters ? defaultFilterSelections.selectedLOBs :
      savedFilters ? savedFilters.networkTaxonomyLOBs :
        overrides?.filterLineOfBusiness ?? filterLineOfBusiness;

    const projectIds = emptyFilters ? defaultFilterSelections.selectedProjects :
      savedFilters ? savedFilters.projectIds :
        overrides?.filterProjects ?? filterProjects;

    const specialtyGroupIds = emptyFilters ? defaultFilterSelections.selectedSpecialties :
      savedFilters ? savedFilters.specialtyGroupIds :
        overrides?.filterSpecialty ?? filterSpecialty;

    const tags = emptyFilters ? [] :
      savedFilters ? savedFilters.tags :
        overrides?.filterTags ?? filterTags;

    const isDefaultFilters = emptyFilters ? true : (
      areSetsEquivalent(getSetOfKeys(networkTaxonomyLOBs), getSetOfKeys(defaultFilterSelections.selectedLOBs)) &&
      areSetsEquivalent(new Set(counties), new Set(defaultFilterSelections.selectedServiceAreas)) &&
      areSetsEquivalent(getSetOfKeys(specialtyGroupIds), getSetOfKeys(defaultFilterSelections.selectedSpecialties)) &&
      areSetsEquivalent(getSetOfKeys(countyStatuses), getSetOfKeys(defaultFilterSelections.selectedStatuses)) &&
      areSetsEquivalent(getSetOfKeys(projectIds), getSetOfKeys(defaultFilterSelections.selectedProjects))
    );

    const filters: EcdFilterStorageItem = {
      counties,
      countyStatuses,
      isDefaultFilters,
      networkTaxonomyLOBs,
      projectIds,
      specialtyGroupIds,
      tags,
    };
    const loadedFilters = JSON.parse(window.localStorage.getItem(localStorageKey) || "{}");
    const storageObj = {
      ...loadedFilters,
      [filterSetName || "default"]: filters,
    };
    window.localStorage.setItem(localStorageKey, JSON.stringify(storageObj));
    getFilterSetNames();
  };

  const saveCustomFilterSet = (filterSetName: string, filters?: EcdFilterStorageItem) => {
    saveFiltersToLocalStorage(filterSetName, false, filters);
  };

  const getFilterSetNames = () => {
    const savedFilters = JSON.parse(window.localStorage.getItem(localStorageKey) || "{}");
    const filters = Object.keys(savedFilters).filter((key) => key !== "default").sort();
    setFilterSetNames(filters);
    return filters;
  }

  const loadCustomFilterSet = (filterSetName?: string): EcdFilterStorageItem => {
    return (JSON.parse(window.localStorage.getItem(localStorageKey) || "{}"))[filterSetName || "default"];
  };

  const applyCustomFilterSet = (filterSet: EcdFilterStorageItem) => {
    setFilterTags(getDefaultSelectedNestedDropdownItems(tagsOptions, filterSet.tags));
    setFilterLineOfBusiness(getDefaultSelectedDropdownItems(LOBOptions, filterSet.networkTaxonomyLOBs.map((s) => s.key)));
    setFilterServiceArea(getDefaultSelectedNestedDropdownItems(serviceAreaOptions, filterSet.counties));
    setFilterSpecialty(getDefaultSelectedDropdownItems(specialtyOptions, filterSet.specialtyGroupIds.map((s) => s.key)));
    setFilterCountyStatus(getDefaultSelectedDropdownItems(countyStatusOptions, filterSet.countyStatuses.map((s) => s.key)));
    setFilterProjects(getDefaultSelectedDropdownItems(projectsOptions, filterSet.projectIds.map((p) => p.key)));

    setHasLoadedFiltersFromStorage(true);
  };

  const deleteCustomFilterSets = (names: string[]) => {
    const loadedFilters = JSON.parse(window.localStorage.getItem(localStorageKey) || "{}");
    names.forEach((n) => {
      delete loadedFilters[n];
    })
    window.localStorage.setItem(localStorageKey, JSON.stringify(loadedFilters));
    getFilterSetNames();
  }

  // T is expected to be DropdownItem or string
  const getDefaultSelectedDropdownItems = <T,>(options: T[], selectedOptions: string[], selectAll?: boolean) => {
    if (options.length > 0) {
      if (selectAll) {
        return options;
      }
      // if it's a DropdownItem
      else if (typeof options[0] === "object") {
        return options.filter((s) => selectedOptions.includes((s as DropdownItem<string>).key)) as T[];
        // if it's a string
      } else {
        return options.filter((s) => selectedOptions.includes(s as string)) as T[];
      }
    }
    return new Array<T>();
  };

  const getDefaultSelectedNestedDropdownItems = (options: NestingParentDropdownItem[], selectedOptions: string[], selectAll?: boolean) => {
    if (selectAll) {
      return options.flatMap((t) => t.children.map((c) => c.key));
    }

    return options.flatMap((t) => {
      return t.children.filter((c) => selectedOptions.includes(c.key)).map((c) => c.key);
    });
  };

  const convertTagStringsToFilters = (filterItems: Partial<FilterItem>[]) => {
    const groups: { [key: string]: NestingParentDropdownItem } = {};
    filterItems.forEach((st) => {
      /* namespacePaths have the format §Tags§{TagGroup}§{TagName}
       * so .split("§") returns ["", "Tags", "{TagGroup}", "{TagName}"]
       */
      const [, , group, tag] = st.namespacePath.split("§");
      if (groups[group]?.children?.length > 0) {
        groups[group].children.push({
          key: `Tags§${group}§${tag}`,
          name: tag,
        });
      } else {
        groups[group] = {
          checkboxStatus: undefined,
          children: [],
          key: group,
          name: group,
        };
        groups[group].children = [{
          key: `Tags§${group}§${tag}`,
          name: tag,
        }];
      }
    });
    return Object.values(groups);
  }

  const convertTagNamespacesToFilterItems = (namespacesPaths: string[]) => {
    return namespacesPaths.map((path) => {
      return {
        "displayName": path.split("§").pop(),
        "itemType": FilterItemType.Leaf,
        "namespacePath": `§${path}`,
        "resourceCount": 0
      }
    });
  }

  const updateDisplayFilters = (overrides?: ApplyFiltersOverrides, freshExcludedIds?: number[]) => {
    const effectiveTags = overrides?.filterTags ?? filterTags;
    const effectiveLineOfBusiness = overrides?.filterLineOfBusiness ?? filterLineOfBusiness;
    const effectiveServiceArea = overrides?.filterServiceArea ?? filterServiceArea;
    const effectiveSpecialty = overrides?.filterSpecialty ?? filterSpecialty;
    const effectiveCountyStatus = overrides?.filterCountyStatus ?? filterCountyStatus;
    const effectiveProjects = overrides?.filterProjects ?? filterProjects;

    const tagsNames = effectiveTags.map((tagString) => {
      const [, group, tag] = tagString.split("§");
      return group + " : " + tag;
    });

    const lineOfBusinessNames = effectiveLineOfBusiness.map((lob) => lob.name);
    const { counties, states } = calculateServiceAreaSelections(serviceAreaOptions, effectiveServiceArea);
    const stateNames = serviceAreaOptions
      .filter((state) => states.includes(state.key))
      .map((state) => state.name);

    const countyNames = serviceAreaOptions
      .flatMap((sa) => sa.children)
      .filter((county) => counties.includes(county.key))
      .map((county) => (
        county.name
      ));
    const specialtiesNames = effectiveSpecialty.map((sp) => sp.name);
    const countyStatusNames = effectiveCountyStatus.map((cs) => cs.name);

    const includedProjectNames: string[] = [];
    const excludedProjectNames: string[] = [];
    const hasProjectsSelected = effectiveProjects.length > 0;
    const projects = hasProjectsSelected ? effectiveProjects : projectsOptions;
    projects.forEach((project) => {
      if ((freshExcludedIds ?? excludedProjectIds).includes(parseInt(project.key))) {
        excludedProjectNames.push(project.name);
      } else {
        includedProjectNames.push(project.name);
      }
    })

    const currentDisplayFilters: DisplayFilters = {
      counties: countyNames,
      countyStatuses: countyStatusNames,
      excludedProjects: excludedProjectNames,
      includedProjects: includedProjectNames,
      includedProjectsTotal: projectsOptions.length,
      lineOfBusiness: lineOfBusinessNames,
      specialties: specialtiesNames,
      specialtyTotal: specialtyOptions.length,
      states: stateNames,
      tags: tagsNames,
      tagsTotal: tagsOptions.flatMap((tagGroup) => tagGroup.children).length
    }

    return currentDisplayFilters;
  }

  // the emptyFilters parameter is necessary because all the set* calls made in resetFilters
  // get batched together with the ones in applyFilters, so they aren't updated by the time newApiReadyFilters is constructed
  // same reasoning goes for saveFiltersToLocalStorage
  const applyFilters = async (emptyFilters?: boolean, overrides?: ApplyFiltersOverrides) => {
    setIsDashboardLoading(true);
    // handling all this up front here so we only have to check emptyFilters once instead of 5 times
    const emptyDefaults = defaultFilterSelections;
    let availableProjectIds: number[], counties: string[], states: string[],
      statuses: RegionSetStatusType[], lobs: string[], specialties: number[];

    // overrides allow dismiss handlers to bypass the stale-closure problem: the handler
    // calls setFilter*(newValue) to keep the UI in sync, then passes the new value here
    // so applyFilters doesn't read the not-yet-committed state value from closure.
    const effectiveProjects = overrides?.filterProjects ?? filterProjects;
    const effectiveServiceArea = overrides?.filterServiceArea ?? filterServiceArea;
    const effectiveCountyStatus = overrides?.filterCountyStatus ?? filterCountyStatus;
    const effectiveLineOfBusiness = overrides?.filterLineOfBusiness ?? filterLineOfBusiness;
    const effectiveSpecialty = overrides?.filterSpecialty ?? filterSpecialty;
    const effectiveTags = overrides?.filterTags ?? filterTags;

    if (emptyFilters) {
      availableProjectIds = emptyDefaults.selectedProjects.map((p) => parseInt(p.key, 10));
      const serviceArea = calculateServiceAreaSelections(serviceAreaOptions, emptyDefaults.selectedServiceAreas);
      counties = serviceArea.counties;
      states = serviceArea.states;
      statuses = emptyDefaults.selectedStatuses.map((f) => f.key as RegionSetStatusType);
      lobs = emptyDefaults.selectedLOBs.map((lob) => lob.key);
      specialties = emptyDefaults.selectedSpecialties.map((sp) => parseInt(sp.key, 10));
    } else {
      availableProjectIds = (effectiveProjects.length > 0 ? effectiveProjects : projectsOptions).map((p) => parseInt(p.key, 10));
      const serviceArea = calculateServiceAreaSelections(serviceAreaOptions, effectiveServiceArea);
      counties = serviceArea.counties;
      states = serviceArea.states;
      statuses = effectiveCountyStatus.map((cs) => cs.key as RegionSetStatusType);
      lobs = effectiveLineOfBusiness.map((lob) => lob.key);
      specialties = effectiveSpecialty.map((sp) => parseInt(sp.key, 10));
    }

    /*
      1. If there are 0 tags and 0 projects selected, send an empty list
      2. If there are > 0 tags and 0 projects selected, send the list returned from the tags api call
      3. If there are 0 tags and > 0 projects selected, send the list of selected project ids
      4. If there are > 0 tags and > 0 projects selected, intersect the response from the tags api call with the list of selected project ids, and send the result
        4a. If the intersection returns 0 results, there's no point in hitting the API, so show empty results
    */
    let includedProjectIds: number[] = [];
    const newExcludedProjectIds: number[] = [];
    // if we intersect the tags response and available projects and get nothing, don't even call the API
    let isIntersectionEmpty = false;
    if (!emptyFilters) {
      if (effectiveTags.length > 0) {
        const tagFilterItems: FilterItemsGroup[] = [
          {
            filters: convertTagNamespacesToFilterItems(effectiveTags),
            operation: FilterItemsGroupOperation.LogicalOr,
          }
        ];
        // first we need to fetch the resource ids that match our tags
        const filteredResources = await resourceFiltrationService.getFilteredProjectResources(tagFilterItems);
        // then we can intersect that with the selected project ids and build the rest of the filters
        const filteredResourceIds = (filteredResources || []).map((fr) => fr.resourceId);
        // case 2 and case 4
        availableProjectIds.forEach((fri) => {
          if (filteredResourceIds.includes(fri)) {
            includedProjectIds.push(fri);
          } else {
            newExcludedProjectIds.push(fri);
          }
        })
        // case 4a
        isIntersectionEmpty = includedProjectIds.length === 0;
      } else if (effectiveProjects.length > 0) {
        // case 3
        includedProjectIds = availableProjectIds;
      } // else, case 1 - apiProjectIds is still []
    }

    // since sending all of something is functionally the same as sending none of something,
    // it's more efficient to send none
    const newApiReadyFilters: EcdApiFilters = {
      // if all service areas are selected, counties will be [] because all the states will be listed instead
      canonicalRegionSetIds: counties.map((c) => parseInt(c, 10)),
      // sending an empty array instead of explicit statuses for county statuses will
      // confuse the api into leaving out opportunity
      countyStatuses: statuses,
      customProjectIds: includedProjectIds.length === projectsOptions.length ? [] : includedProjectIds,
      networkTaxonomyLOBs: lobs.length === LOBOptions.length ? [] : lobs,
      projectRunConfigurationTypes: [],
      specialtyGroupIds: specialties.length === specialtyOptions.length ? [] : specialties,
      stateAbbreviations: states.length === serviceAreaOptions.length ? [] : states,
    };

    const newDisplayFilters = updateDisplayFilters(overrides, newExcludedProjectIds);

    setExcludedProjectIds(newExcludedProjectIds);
    setApiReadyFilters(newApiReadyFilters);
    setForceNoResults(isIntersectionEmpty);
    saveFiltersToLocalStorage(undefined, emptyFilters, undefined, overrides);
    setDisplayFilters(newDisplayFilters);

    // set this back to false so we can use it again later
    setHasLoadedFiltersFromStorage(false);
  }

  const resetFilters = () => {
    const defaults = defaultFilterSelections;

    setFilterTags([]);
    setFilterLineOfBusiness(defaults.selectedLOBs);
    setFilterServiceArea(defaults.selectedServiceAreas);
    setFilterSpecialty(defaults.selectedSpecialties);
    setFilterCountyStatus(defaults.selectedStatuses);
    setFilterProjects(defaults.selectedProjects);

    applyFilters(true);
  }

  const defaultFilterSelections = useMemo(() => {
    const selectedLOBs = getDefaultSelectedDropdownItems(LOBOptions, [], true);
    const selectedServiceAreas = getDefaultSelectedNestedDropdownItems(serviceAreaOptions, [], true);
    const selectedSpecialties = getDefaultSelectedDropdownItems(specialtyOptions, [], true);
    const selectedStatuses = getDefaultSelectedDropdownItems(countyStatusOptions, defaultCountyStatuses);
    const selectedProjects = getDefaultSelectedDropdownItems(projectsOptions, [], true);

    return {
      selectedLOBs,
      selectedProjects,
      selectedServiceAreas,
      selectedSpecialties,
      selectedStatuses,
    };
  }, [LOBOptions, serviceAreaOptions, specialtyOptions, countyStatusOptions, projectsOptions]);

  const getSetOfKeys = (items: DropdownItem<string>[]) => {
    return new Set(items.map((item) => item.key));
  }


  const getFiltersSavedSet = () => {
    // naming things is hard, and a Set of keys is not the same thing as a "set" of saved filters
    const tagKeys = new Set(filterTags);
    const lobKeys = getSetOfKeys(filterLineOfBusiness);
    const serviceAreaKeys = new Set(filterServiceArea);
    const specialtyKeys = getSetOfKeys(filterSpecialty);
    const statusKeys = getSetOfKeys(filterCountyStatus);
    const projectKeys = getSetOfKeys(filterProjects);

    let index = 0;
    while (index < filterSetNames.length) {
      const filters = loadCustomFilterSet(filterSetNames[index]);
      if (
        areSetsEquivalent(tagKeys, new Set(filters.tags)) &&
        areSetsEquivalent(lobKeys, getSetOfKeys(filters.networkTaxonomyLOBs)) &&
        areSetsEquivalent(serviceAreaKeys, new Set(filters.counties)) &&
        areSetsEquivalent(specialtyKeys, getSetOfKeys(filters.specialtyGroupIds)) &&
        areSetsEquivalent(statusKeys, getSetOfKeys(filters.countyStatuses)) &&
        areSetsEquivalent(projectKeys, getSetOfKeys(filters.projectIds))
      ) {
        return filterSetNames[index];
      }
      index += 1;
    }

    return "";
  }

  return (
    <EnterpriseComplianceFiltersContext.Provider
      value={{
        apiReadyFilters,
        applyCustomFilterSet,
        applyFilters,
        countyStatusOptions,
        deleteCustomFilterSets,
        displayFilters,
        exceptionsTimeFrame,
        excludedProjectIds,
        filterCountyStatus,
        filterLineOfBusiness,
        filterProjects,
        filterServiceArea,
        filterSetNames,
        filterSpecialty,
        filterTags,
        forceNoResults,
        gapOverviewTimeFrame,
        gapsByAgeTimeFrame,
        getFilterSetNames,
        hasLoadedFiltersFromStorage,
        isDashboardLoading,
        loadCustomFilterSet,
        loadedFilterSet,
        LOBOptions,
        projectsOptions,
        resetFilters,
        saveCustomFilterSet,
        serviceAreaOptions,
        setDisplayFilters,
        setExceptionsTimeFrame,
        setFilterCountyStatus,
        setFilterLineOfBusiness,
        setFilterProjects,
        setFilterServiceArea,
        setFilterSpecialty,
        setFilterTags,
        setGapOverviewTimeFrame,
        setGapsByAgeTimeFrame,
        setIsDashboardLoading,
        specialtyOptions,
        tagsOptions,
      }}>
      {props.children}
    </EnterpriseComplianceFiltersContext.Provider>
  )
}

export default EnterpriseComplianceFiltersContextProvider;